import { HttpClient } from 'api/http-client';
import { omit } from 'lodash';
import {
  CreateKnowledgeBaseFolder,
  KnowledgeBaseFolder,
  KnowledgeBaseFolderLite,
  KnowledgeBaseTreeFolder,
  UpdateKnowledgeBaseFolder,
  CreateKnowledgeBaseAttachment,
  KnowledgeBaseAttachment,
  WebsiteContentResponse,
  AddWebsiteToKnowledgeBasePayload,
  AddAiPagesPayload,
  UpdateAiPagePayload,
} from './types';

class KnowlegdeBaseClient extends HttpClient {
  public async getKnowledgeBaseFolders(): Promise<KnowledgeBaseFolderLite[]> {
    return this.get<KnowledgeBaseFolderLite[]>('/knowledge-base-folders');
  }
  public async getKnowledgeBaseFolder(
    folderId: number,
  ): Promise<KnowledgeBaseFolder> {
    return this.get<KnowledgeBaseFolder>(`/knowledge-base-folders/${folderId}`);
  }
  public async createKnowledgeBaseFolder(
    payload: CreateKnowledgeBaseFolder,
  ): Promise<KnowledgeBaseFolder> {
    return this.post<KnowledgeBaseFolder, CreateKnowledgeBaseFolder>(
      '/knowledge-base-folders',
      payload,
    );
  }
  public async updateKnowledgeBaseFolder(
    folderId: number,
    payload: UpdateKnowledgeBaseFolder,
  ): Promise<KnowledgeBaseFolder> {
    return this.put(`/knowledge-base-folders/${folderId}`, payload);
  }
  public async deleteKnowledgeBaseFolder(folderId: number) {
    return this.delete(`/knowledge-base-folders/${folderId}`);
  }
  public async deleteKnowledgeBaseFolderContent(
    folderId: number,
    contentId: number,
  ): Promise<void> {
    return this.delete<void>(
      `/knowledge-base-folders/${folderId}/contents/${contentId}`,
    );
  }
  public async getKnowledgeBaseFolderTree(
    folderId: number,
  ): Promise<KnowledgeBaseTreeFolder> {
    return this.get<KnowledgeBaseTreeFolder>(
      `/knowledge-base-folders/${folderId}/tree`,
    );
  }

  public async createKnowledgeBaseAttachment(
    folderId: number,
    file: File,
    info: CreateKnowledgeBaseAttachment,
  ): Promise<KnowledgeBaseAttachment> {
    const formData = new FormData();
    const fileMeta = {
      attachment: info,
    };
    const blob = new Blob([JSON.stringify(fileMeta)], {
      type: 'application/json',
    });

    formData.append('file', file);
    formData.append('request', blob);

    return this.post<KnowledgeBaseAttachment, FormData>(
      `/knowledge-base-folders/${folderId}/attachment`,
      formData,
    );
  }
  public async getContentFromWebsiteUrl(url: string) {
    return this.post<WebsiteContentResponse, void>(
      `/web-scraper/map?url=${url}`,
    );
  }
  public async addWebsiteToKnowledgeBase(
    payload: AddWebsiteToKnowledgeBasePayload,
  ): Promise<void> {
    return this.post<void, Omit<AddWebsiteToKnowledgeBasePayload, 'folder_id'>>(
      `knowledge-base-folders/${payload.folder_id}/web-scraper/selections`,
      omit(payload, 'folder_id'),
    );
  }
  public async addAiPages(payload: AddAiPagesPayload): Promise<void> {
    return this.post<void, Omit<AddAiPagesPayload, 'folder_id'>>(
      `knowledge-base-folders/${payload.folder_id}/ai-files`,
      omit(payload, 'folder_id'),
    );
  }
  public async updateAiPage(payload: UpdateAiPagePayload): Promise<void> {
    return this.patch<
      void,
      Omit<UpdateAiPagePayload, 'folder_id' | 'content_id'>
    >(
      `knowledge-base-folders/${payload.folder_id}/ai-files/${payload.content_id}`,
      omit(payload, 'folder_id', 'ai_file_id'),
    );
  }
}

export const knowledgeBaseAPI = new KnowlegdeBaseClient();
